import { Draggable } from '@hello-pangea/dnd';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Avatar, AvatarGroup, Box, Chip, Divider, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { memo, useEffect, useState } from 'react';
import ImageLightBox from '../../components/ImageLightBox';
import ChecklistCount from '../ChecklistCount';
import CustomListItemText from '../CustomListItemText';
import Iconify from '../Iconify';
import KanbanCardDrawer from '../KanbanCardDrawer';
import RandomChips from '../RandomChips';
import socket from '../../hooks/useSocket';
import api from '../../services/api';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ToastSuccess from "../../toast/success/toastSuccess";
import ToastError from "../../toast/error/toastError";
import KanbanTransferCardModal from '../KanbanTransferCardModal';

function KanbanCard({ index, card, columnId, column, filters, assigneds, priorities, cardTags, taskTitle }) {
    const theme = useTheme();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [numberComments, setNumberComments] = useState(0)
    const [anchorEl, setAnchorEl] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showCopyButton, setShowCopyButton] = useState(false);
    const [transferCard, setTransferCard] = useState(false);
    const { id, title, comments = [], attachments = [], kanbanPriorityLevel, description, creator, assigned, tasks, tags, taskNumber, requester } = card;
    useEffect(() => {
        // Handler nomeado para permitir o off() no cleanup.
        // Antes era arrow inline → impossível remover.
        // KanbanCard é renderizado em listas (1 listener por card que entra/sai).
        const handleKanbanCommentsNumber = (data) => {
            if (data?.cardId === cardProps.id) {
                setNumberComments(data.commentCount);
            }
        };

        socket.on("kanban-comments-number", handleKanbanCommentsNumber);

        return () => {
            socket.off("kanban-comments-number", handleKanbanCommentsNumber);
        };
    }, [card.id]);

    useEffect(() => {
        setNumberComments(comments?.length || 0)
    }, [comments])

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setDropdownOpen(true);
    };

    const handleMenuClose = (event) => {
        event.stopPropagation();
        setAnchorEl(null);
        setDropdownOpen(false);
    };

    const archiveCard = async (id) => {
        try {
            await api.delete(`/kanban/card/${id}`, {
                params: { action: 'archive' },
            });
        } catch (err) {
            console.error(err);
        }
    };

    const handleOpenTransferCard = () => {
        setTransferCard(true);
    };

    const handleCloseTransferCard = () => {
        setTransferCard(false);
    };

    const handleTransferCard = async (kanbanCardData) => {
        try {
            const { kanbanId, columnId, isChangeKanban } = kanbanCardData;
            if (isChangeKanban) {
                await api.put(`/kanban/card/transfer/${card.id}`, {
                    kanbanId, columnId
                });
            } else {
                await api.put(`/kanban/card/${card.id}`, {
                    columnId
                });
            }
        } catch (err) {
            console.error(err)
        }
    };

    const copyToClipboard = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(taskNumber).then(() => {
            ToastSuccess(`${taskTitle.charAt(0).toUpperCase() + taskTitle.slice(1)} copiado!`);
        }).catch(err => {
            ToastError('Erro ao copiar: ' + err.message);
        });
    };


    return (
        <>
            <Draggable draggableId={id} index={index}>
                {(provided, snapshot) => (
                    <Container
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        $isDragging={snapshot.isDragging}
                        $themeMode={theme.palette.mode}
                        sx={{
                            background: `linear-gradient(to right, ${column.color} 8px, transparent 8px)`,
                            backgroundColor: (theme) => (theme.palette.mode === 'light' ? '#ffffff' : '#161c24'),
                        }}
                    >
                        <Box
                            onClick={() => setDrawerOpen(true)}

                        >
                            {attachments.length > 0 && (
                                <ImageLightBox src={attachments[0]} alt={attachments[0]} />
                            )}
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    {kanbanPriorityLevel && (
                                        <>
                                            {filters?.viewCardPriority
                                                ?
                                                <Chip
                                                    label={kanbanPriorityLevel.title}
                                                    size="small"
                                                    variant="contained"
                                                    sx={{
                                                        color: (theme) => theme.palette.getContrastText(kanbanPriorityLevel.color),
                                                        backgroundColor: kanbanPriorityLevel.color,
                                                        borderRadius: '5px',
                                                        height: '22px',
                                                        marginRight: '5px',
                                                    }}
                                                />
                                                :
                                                <Tooltip title={kanbanPriorityLevel.title}>
                                                    <Box
                                                        sx={{
                                                            width: '40px',
                                                            height: '10px',
                                                            backgroundColor: kanbanPriorityLevel.color,
                                                            borderRadius: '5px',
                                                            cursor: 'pointer',
                                                            marginRight: '5px',
                                                            marginBottom: '5px'
                                                        }}
                                                    />
                                                </Tooltip>
                                            }
                                        </>
                                    )}
                                    {requester && (
                                        <Tooltip title={"Solicitante"} placement="right">
                                            <StyledChip
                                                sx={{ height: '22px' }}
                                                sixe="small"
                                                avatar={<Avatar alt={requester.name} src={requester.profilePicUrl} />}
                                                label={requester.name.length > 25 ? requester.name.substring(0, 25) + '...' : requester.name}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>
                                <IconButton aria-label="delete" onClick={handleMenuOpen}>
                                    <MoreVertIcon />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={dropdownOpen}
                                    onClose={e => handleMenuClose(e)}
                                >
                                    <MenuItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenTransferCard();
                                            handleMenuClose(e);
                                        }}
                                        sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}
                                    >
                                        <Iconify icon="solar:arrow-right-up-bold-duotone" />
                                        <Box sx={{ ml: 1 }}>Transferir Tarefa</Box>
                                    </MenuItem>
                                    <MenuItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            archiveCard(id);
                                            handleMenuClose(e);
                                        }}
                                        sx={{ color: 'warning.main', display: 'flex', alignItems: 'center' }}
                                    >
                                        <Iconify icon="solar:archive-down-bold" />
                                        <Box sx={{ ml: 1 }}>Arquivar Tarefa</Box>
                                    </MenuItem>
                                </Menu>
                            </Box>

                            <Box mb={1} display="flex" alignItems="center" justifyContent="space-between">
                                <CustomListItemText title={title} description={description} />
                            </Box>

                            {filters?.showTaskNumber && (
                                <Tooltip title={`Copiar Nº ${taskTitle.charAt(0).toUpperCase() + taskTitle.slice(1)}`} placement="right">
                                    <Box
                                        mb={1}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        onMouseEnter={() => setShowCopyButton(true)}
                                        onMouseLeave={() => setShowCopyButton(false)}
                                    >
                                        <Box display="flex" alignItems="center" color={theme.palette.text.secondary}>
                                            <Iconify icon="ant-design:alert-twotone" width={16} height={16} sx={{ color: '#ecff33' }} />
                                            <Typography variant="caption" ml={0.5}>{`${taskNumber}`}</Typography>
                                        </Box>

                                        {showCopyButton && (
                                            <IconButton
                                                size="small"
                                                onClick={copyToClipboard}
                                                sx={{ padding: 0, marginRight: '10px' }}
                                            >
                                                <ContentCopyIcon
                                                    sx={{
                                                        width: '15px',
                                                        height: '15px'
                                                    }}
                                                />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Tooltip>
                            )}
                            {(filters?.showTags && tags?.length > 0) && (
                                <Box mt={1} display="flex" alignItems="center" justifyContent="flex-start" flexWrap="wrap" gap={0.5}>
                                    {tags.map(tag => (
                                        <>
                                            {
                                                /*
                                                * Este campo existe temporariamente até que o campo onde esta marcado true vire um parametro.
                                                */
                                            }
                                            {true ?
                                                <Chip
                                                    key={index}
                                                    label={tag.title}
                                                    size="small"
                                                    sx={{
                                                        color: (theme) => theme.palette.getContrastText(tag.color),
                                                        backgroundColor: tag.color,
                                                        height: '20px',
                                                        borderRadius: '5px'
                                                    }}
                                                />
                                                :
                                                <Tooltip title={tag.title} key={tag.id}>
                                                    <Box
                                                        key={index}
                                                        label={tag.title}
                                                        sx={{
                                                            borderRadius: '10px',
                                                            padding: '5px 20px',
                                                            color: (theme) => theme.palette.getContrastText(tag.color),
                                                            backgroundColor: tag.color
                                                        }}
                                                    />
                                                </Tooltip>}
                                        </>
                                    ))}
                                </Box>
                            )}
                            {creator && filters?.showCreatedBy === true && (
                                <Box mt={1} display="flex" alignItems="center" justifyContent="space-between">
                                    <Tooltip title={"Criador da solicitação"} placement="right">
                                        <StyledChip
                                            sx={{ height: '22px' }}
                                            sixe="small"
                                            avatar={<Avatar alt={creator.name} src={creator.media} />}
                                            label={creator.name}
                                        />
                                    </Tooltip>
                                </Box>
                            )}
                            {tasks?.length > 0 && (
                                <Box mt={1} display="flex" alignItems="center" justifyContent="space-between">
                                    <ChecklistCount tasks={tasks} />
                                </Box>
                            )}
                            <Divider sx={{ marginTop: '10px' }} />
                            <Box mt={1} display="flex" alignItems="center" justifyContent="space-between">
                                <Box display="flex" alignItems="center" color={theme.palette.text.secondary}>
                                    <Iconify icon="uim:comment-dots" width={16} height={16} />
                                    <Typography variant="caption" ml={0.5}>{numberComments}</Typography>
                                </Box>
                                {assigned?.length > 0 && (
                                    <Tooltip
                                        placement="top"
                                        arrow
                                        title={`Atribuido a ${assigned.map((user) => user.name).join(', ')}`}
                                    >
                                        <AvatarGroup
                                            max={4}
                                            sx={{
                                                '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 15 },
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {assigned.map((user) => (
                                                <Avatar key={user.name} src={user.media} />
                                            ))}
                                        </AvatarGroup>
                                    </Tooltip>
                                )}

                            </Box>
                        </Box>
                    </Container>
                )}
            </Draggable >
            <KanbanTransferCardModal
                columnId={columnId}
                card={card}
                open={transferCard}
                onClose={handleCloseTransferCard}
                onConfirm={handleTransferCard}
            />
            <KanbanCardDrawer
                priorities={priorities}
                cardTags={cardTags}
                assigneds={assigneds}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                cardId={card.id}
            />
        </>
    );
}

export default memo(KanbanCard);

const Container = styled(Box)(({ theme, $isDragging, $themeMode }) => ({
    width: 320,
    borderRadius: 12,
    padding: '0px 5px 10px 20px',
    marginBottom: 10,
    fontWeight: 400,
    fontSize: 12,
    backgroundColor: $themeMode === 'light'
        ? $isDragging
            ? 'rgba(255, 255, 255, 0.48)'
            : 'rgb(255, 255, 255)'
        : $isDragging
            ? 'rgba(22, 28, 36, 0.48)'
            : 'rgb(22, 28, 36)',
    backdropFilter: $isDragging ? 'blur(6px)' : 'none',
    '&:hover': {
        boxShadow: $themeMode === 'light'
            ? 'rgba(145, 158, 171, 0.16) 0px 20px 40px -4px'
            : 'rgba(0, 0, 0, 0.16) 0px 20px 40px -4px',
    },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
    borderRadius: '5px',
    fontSize: '0.875rem',
    fontWeight: 500,
    backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1b212c' : '#ffffff',
    '& .MuiChip-avatar': {
        width: 18,
        height: 18,
    },
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));
